'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Input Variants
 *
 * Defines visual styles for the Input component using design tokens.
 * Variants: text, email, password, number, search, tel, datetime-local
 * States: default, error, success, disabled
 */
const inputVariants = cva(
  // Base styles
  'flex h-10 w-full rounded-md border px-3 py-2 text-base transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-neutral-100 dark:disabled:bg-neutral-800',
  {
    variants: {
      variant: {
        text: 'bg-white text-neutral-900 border-border-primary placeholder:text-neutral-400 hover:border-border-secondary dark:bg-neutral-900 dark:text-white dark:border-border-primary dark:placeholder:text-neutral-500 dark:hover:border-border-secondary',
        email:
          'bg-white text-neutral-900 border-border-primary placeholder:text-neutral-400 hover:border-border-secondary dark:bg-neutral-900 dark:text-white dark:border-border-primary dark:placeholder:text-neutral-500 dark:hover:border-border-secondary',
        password:
          'bg-white text-neutral-900 border-border-primary placeholder:text-neutral-400 hover:border-border-secondary dark:bg-neutral-900 dark:text-white dark:border-border-primary dark:placeholder:text-neutral-500 dark:hover:border-border-secondary',
        number:
          'bg-white text-neutral-900 border-border-primary placeholder:text-neutral-400 hover:border-border-secondary dark:bg-neutral-900 dark:text-white dark:border-border-primary dark:placeholder:text-neutral-500 dark:hover:border-border-secondary',
        search:
          'bg-white text-neutral-900 border-border-primary placeholder:text-neutral-400 hover:border-border-secondary dark:bg-neutral-900 dark:text-white dark:border-border-primary dark:placeholder:text-neutral-500 dark:hover:border-border-secondary',
      },
      state: {
        default: 'border-border-primary',
        error: 'border-error-500 focus-visible:ring-error-500',
        success: 'border-success-500 focus-visible:ring-success-500',
      },
    },
    defaultVariants: {
      variant: 'text',
      state: 'default',
    },
  }
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>,
    VariantProps<typeof inputVariants> {
  /** Input type */
  type?: 'text' | 'email' | 'password' | 'number' | 'search' | 'tel' | 'datetime-local' | 'color' | 'file';
  /** Icon to display on the left side */
  leftIcon?: React.ReactNode;
  /** Icon to display on the right side */
  rightIcon?: React.ReactNode;
  /** Error message to display below the input */
  error?: string;
  /** Success message to display below the input */
  success?: string;
  /** Label for the input */
  label?: string;
  /** Helper text to display below the input */
  helperText?: string;
  /** If true, displays a required indicator */
  required?: boolean;
}

/**
 * Input Component
 *
 * A versatile input component with multiple variants and states.
 * Supports icons, error/success states, labels, and helper text.
 * Fully typed with TypeScript and supports form integration.
 *
 * @example
 * ```tsx
 * // Basic text input
 * <Input type="text" placeholder="Enter text..." />
 *
 * // Email input with label
 * <Input
 *   type="email"
 *   label="Email Address"
 *   placeholder="user@example.com"
 *   required
 * />
 *
 * // Input with icons
 * <Input
 *   type="search"
 *   placeholder="Search..."
 *   leftIcon={<Search />}
 *   rightIcon={<X />}
 * />
 *
 * // Input with error state
 * <Input
 *   type="text"
 *   value={email}
 *   state="error"
 *   error="Invalid email format"
 * />
 *
 * // Input with success state
 * <Input
 *   type="text"
 *   state="success"
 *   success="Username is available"
 * />
 *
 * // Password input
 * <Input type="password" label="Password" placeholder="••••••••" />
 *
 * // With helper text
 * <Input
 *   type="text"
 *   label="Username"
 *   helperText="3-20 characters, alphanumeric only"
 * />
 *
 * // With react-hook-form
 * <Input
 *   type="text"
 *   placeholder="Enter value"
 *   {...register('fieldName')}
 * />
 * ```
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = 'text',
      variant,
      state = 'default',
      leftIcon,
      rightIcon,
      error,
      success,
      label,
      helperText,
      required,
      disabled,
      ...props
    },
    ref
  ) => {
    // Determine state based on error/success if not explicitly set
    const resolvedState = error ? 'error' : success ? 'success' : state;

    return (
      <div className="w-full">
        {/* Label */}
        {label && (
          <label className="mb-2 block text-sm font-medium text-text-primary">
            {label}
            {required && <span className="ml-1 text-error-500">*</span>}
          </label>
        )}

        {/* Input Container */}
        <div className="relative flex items-center">
          {/* Left Icon */}
          {leftIcon && (
            <span className="pointer-events-none absolute left-3 flex h-full items-center text-neutral-400 dark:text-neutral-500">
              {leftIcon}
            </span>
          )}

          {/* Input Field */}
          <input
            type={type}
            className={cn(
              inputVariants({ variant, state: resolvedState }),
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              className
            )}
            ref={ref}
            disabled={disabled}
            {...props}
          />

          {/* Right Icon */}
          {rightIcon && (
            <span className="pointer-events-none absolute right-3 flex h-full items-center text-neutral-400 dark:text-neutral-500">
              {rightIcon}
            </span>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <p className="mt-1 text-sm text-error-500">
            {error}
          </p>
        )}

        {/* Success Message */}
        {success && (
          <p className="mt-1 text-sm text-success-500">
            {success}
          </p>
        )}

        {/* Helper Text */}
        {helperText && !error && !success && (
          <p className="mt-1 text-sm text-text-secondary">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input, inputVariants };
